import {
  type KickDto,
  type ZoneDto,
  FREE_KITS,
  TEAM_NAME_MAX,
  kitById,
  normalizeTeamName,
  teamNameError,
} from '@penalty-cup/shared';
import { type ShotInput, ZONES, resolvePenalty } from '@penalty-cup/game-engine';
import { useState } from 'react';
import { Goal } from '../components/Goal.js';
import { Meter } from '../components/Meter.js';
import { Jersey } from '../components/Jersey.js';
import { useApp } from '../store.js';
import { OUTCOME_TITLE, QUALITY_LABEL } from '../strings.js';

const random = () => Math.random();
const randomZone = () => ZONES[Math.floor(Math.random() * ZONES.length)] as ZoneDto;

/** Удар на тренировке: тот же движок, что на сервере, но локально — без статистики и наград. */
function trainingKick(shot: ShotInput, keeperZone: ZoneDto, shooter: 'A' | 'B'): KickDto {
  const r = resolvePenalty(shot, { zone: keeperZone, reaction: 0.5 }, random);
  return {
    seq: 0,
    round: 1,
    shooter,
    shotZone: shot.zone,
    keeperZone,
    quality: r.quality,
    outcome: r.outcome,
    autoShot: false,
    autoKeeper: false,
  };
}

function TrainingShot({ onDone }: { onDone: () => void }) {
  const [zone, setZone] = useState<ZoneDto | null>(null);
  const [power, setPower] = useState<number | null>(null);
  const [kick, setKick] = useState<KickDto | null>(null);
  const stage = kick ? 'result' : power !== null ? 'accuracy' : zone ? 'power' : 'zone';
  const tips = {
    zone: 'Выбери угол. Верхние почти не берутся, но чаще летят мимо',
    power: 'Останови силу. Красная зона — перебор, мяч улетит',
    accuracy: 'Останови стрелку в центре — будет идеальный удар',
    result: '',
  };
  return (
    <>
      <h1 className="screen__title">Тренировка: удар</h1>
      <p className="screen__lead">{tips[stage]}</p>
      <Goal
        selectable={stage === 'zone'}
        selected={zone ?? undefined}
        onSelect={setZone}
        kick={kick}
        hint={stage === 'zone' ? 'shoot' : null}
        keeperColor="#888"
      />
      <section className="match__panel">
        {stage === 'power' && <Meter kind="power" onStop={setPower} />}
        {stage === 'accuracy' && (
          <Meter
            kind="accuracy"
            onStop={(accuracy) =>
              setKick(trainingKick({ zone: zone!, power: power!, accuracy }, randomZone(), 'A'))
            }
          />
        )}
        {kick && (
          <div className={`result ${kick.outcome === 'GOAL' ? 'result--good' : 'result--bad'}`} role="status">
            <span className="result__title">{OUTCOME_TITLE[kick.outcome]}</span>
            <span className="result__note">{QUALITY_LABEL[kick.quality]}</span>
            <button type="button" className="play play--small" onClick={onDone}>
              Дальше
            </button>
          </div>
        )}
      </section>
    </>
  );
}

function TrainingSave({ onDone, kitColor }: { onDone: () => void; kitColor: string }) {
  const [kick, setKick] = useState<KickDto | null>(null);
  const [zone, setZone] = useState<ZoneDto | null>(null);
  const dive = (z: ZoneDto) => {
    setZone(z);
    setKick(trainingKick({ zone: randomZone(), power: 0.7, accuracy: 0.7 }, z, 'B'));
  };
  return (
    <>
      <h1 className="screen__title">Тренировка: сейв</h1>
      <p className="screen__lead">{kick ? '' : 'Ты в воротах. Угадай, куда пробьют, — прыгни туда'}</p>
      <Goal
        selectable={!kick}
        selected={zone ?? undefined}
        onSelect={dive}
        kick={kick}
        hint={kick ? null : 'keep'}
        keeperColor={kitColor}
      />
      <section className="match__panel">
        {kick && (
          <div className={`result ${kick.outcome === 'GOAL' ? 'result--bad' : 'result--good'}`} role="status">
            <span className="result__title">{OUTCOME_TITLE[kick.outcome]}</span>
            <button type="button" className="play play--small" onClick={onDone}>
              В игру!
            </button>
          </div>
        )}
      </section>
    </>
  );
}

/** Welcome → имя команды → форма → тренировка удара → тренировка сейва (docs/spec/03, экраны 2–6). */
export function Onboarding() {
  const me = useApp((s) => s.me);
  const step = useApp((s) => s.onboardingStep);
  const saveProfile = useApp((s) => s.saveProfile);
  const setStep = useApp((s) => s.setOnboardingStep);
  const [local, setLocal] = useState<'welcome' | 'name' | 'kit'>('welcome');
  const [name, setName] = useState(() => normalizeTeamName(me?.firstName ?? '').slice(0, TEAM_NAME_MAX));
  const [kit, setKit] = useState(me?.kit ?? 'orange');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const kitColor = kitById(kit).color;

  if (step === 'trainShot') {
    return (
      <main className="screen onboarding">
        <TrainingShot onDone={() => setStep('trainSave')} />
        <button type="button" className="link" onClick={() => setStep(null)}>
          Пропустить тренировку
        </button>
      </main>
    );
  }
  if (step === 'trainSave') {
    return (
      <main className="screen onboarding">
        <TrainingSave kitColor={kitColor} onDone={() => setStep(null)} />
      </main>
    );
  }

  if (local === 'welcome') {
    return (
      <main className="screen onboarding onboarding--welcome">
        <div className="home__hero" aria-hidden="true">
          ⚽
        </div>
        <h1 className="home__title">Penalty Cup</h1>
        <p className="screen__lead">
          Турниры по пенальти на 8 игроков. Бей, отбивай, обыгрывай соперников — и забирай кубок.
        </p>
        <button type="button" className="play" onClick={() => setLocal('name')}>
          Начать
        </button>
      </main>
    );
  }

  if (local === 'name') {
    const problem = teamNameError(name);
    return (
      <main className="screen onboarding">
        <h1 className="screen__title">Название команды</h1>
        <p className="screen__lead">Так тебя увидят соперники в сетке турнира</p>
        <input
          className="field"
          value={name}
          maxLength={TEAM_NAME_MAX + 5}
          onChange={(e) => setName(e.target.value)}
          aria-label="Название команды"
          aria-invalid={!!problem}
          autoFocus
        />
        <p className="field__hint">{problem ?? `${normalizeTeamName(name).length} из ${TEAM_NAME_MAX}`}</p>
        <button
          type="button"
          className="play play--small"
          disabled={!!problem}
          onClick={() => setLocal('kit')}
        >
          Дальше
        </button>
      </main>
    );
  }

  return (
    <main className="screen onboarding">
      <h1 className="screen__title">Форма</h1>
      <p className="screen__lead">{normalizeTeamName(name)}</p>
      <div className="kits" role="radiogroup" aria-label="Форма">
        {FREE_KITS.map((k) => (
          <button
            key={k.id}
            type="button"
            role="radio"
            aria-checked={kit === k.id}
            aria-label={k.name}
            className={`kit ${kit === k.id ? 'kit--selected' : ''}`}
            onClick={() => setKit(k.id)}
          >
            <Jersey kit={k.id} />
          </button>
        ))}
      </div>
      {error && <p className="field__hint field__hint--error">{error}</p>}
      <button
        type="button"
        className="play play--small"
        disabled={saving}
        onClick={async () => {
          setSaving(true);
          setError(null);
          try {
            await saveProfile({ teamName: normalizeTeamName(name), kit }, true);
          } catch {
            setError('Не удалось сохранить. Попробуйте ещё раз');
          } finally {
            setSaving(false);
          }
        }}
      >
        {saving ? 'Сохраняем…' : 'На тренировку'}
      </button>
    </main>
  );
}
